import React from 'react';
import { isPlatform, IonLoading, IonGrid } from '@ionic/react';
import { GoogleMap } from '../../../components/shared/GoogleMap/map/map';
import {
  MapType,
  DirectionsWaypoint,
  DirectionLocation
} from '../../../components/shared/GoogleMap/map/map.type';
import './LoadMap.scss';
import { PCMilerLoadMap } from '../../../components/shared/PCMilerMap';
import { IONICWrapper } from '../../../utils/common';
import { PCmiler } from '../../../utils/constants';

export interface ILoadStops {
  appointmentFrom?: Date;
  appointmentTo?: Date;
  addressLine1?: string;
  city: string;
  country: string;
  deadHead?: string;
  postalCode?: string;
  specialServices?: any[];
  state: string;
  stopId: string;
  stopType: string; // P-Pickup D- Drop
  timeZone?: string;
  stopSequence?: number;
  sequenceNo?: number;
  longitude?: any;
  latitude?: any;
  locationName?: string;
}
export interface ILoadMapProps {
  loadStop: ILoadStops[];
  isNetworkAvailable?: boolean;
  isCarrierLoad: boolean;
  shipmentId: any;
  fullScreenControl?: boolean;
}

export interface ILoadMapState {
  usePCmilerMaps: boolean;
  isShowMap: boolean;
  origin: DirectionLocation;
  destination: DirectionLocation;
  waypoints: DirectionsWaypoint[];
  isMapVisible: boolean;
  isShowAddrInfoWindow: boolean;
}

export class LoadMap extends React.PureComponent<ILoadMapProps, ILoadMapState> {
  constructor(props: ILoadMapProps) {
    super(props);
    this.state = {
      usePCmilerMaps: false,
      isShowMap: false,
      origin: {
        locationName: '',
        locationType: '',
        index: -1,
        isMarkerAdded: false,
        latLng: { lat: 0, lng: 0 },
        postalCode: '',
        city: '',
        state: '',
        shipmentId: '',
        stopId: '',
        addressLine1 : null
      },
      destination: {
        locationName: '',
        locationType: '',
        index: -1,
        isMarkerAdded: false,
        latLng: { lat: 0, lng: 0 },
        postalCode: '',
        city: '',
        state: '',
        shipmentId: '',
        stopId: '',
        addressLine1 : null
      },
      waypoints: [],
      isMapVisible: false,
      isShowAddrInfoWindow: true
    };
  }

  componentDidMount() {
    const _usePCmilerMaps = IONICWrapper.details.keys 
      && IONICWrapper.details.keys.configuration.usePCmilerMaps 
        || PCmiler.usePCmilerMaps;
    const isPCmilerMaps = _usePCmilerMaps === "true" ? true : false;
        
    const locationDetails = this.getLocations(this.props);
    this.setState({
      usePCmilerMaps: isPCmilerMaps,
      waypoints: locationDetails.waypoints || [],
      isShowMap: isPCmilerMaps ? false : true,
      isMapVisible: isPCmilerMaps ? true : false,
      origin: locationDetails.origin!,
      destination: locationDetails.destination!,
    },
      () => {          
        isPCmilerMaps ? this.setIsShowMap() : this.isMapVisible()
      }
    );
  }

  componentDidUpdate(prevProps: ILoadMapProps): void {
    const isPCmilerMaps = this.state.usePCmilerMaps;
    const locationDetails = this.getLocations(this.props);
    const oldLocationDetails = this.getLocations(prevProps);
    if (
      JSON.stringify(locationDetails) !== JSON.stringify(oldLocationDetails)
    ) {
      this.setState({
        waypoints: locationDetails.waypoints || [],
        isShowMap: isPCmilerMaps ? false : true,
        isMapVisible: isPCmilerMaps ? true : false,
        origin: locationDetails.origin!,
        destination: locationDetails.destination!,
      },
        () => {
          isPCmilerMaps ? this.setIsShowMap() : this.isMapVisible()
        }
      );
    }
  }

  isMapVisible(){
    setTimeout(() => {
      this.setState({ isMapVisible: true });
    }, 500);
  }
  
  setIsShowMap() {
    setTimeout(() => {
      this.setState({ isShowMap: true });
    }, 500);
  }

  getLocationName = (data: ILoadStops): string => {
    const locationName: string =
      (data.locationName && `${data.locationName}, `) || '';
    const city: string = (data.city && `${data.city}, `) || '';
    const state: string = (data.state && `${data.state}, `) || '';
    const addressLine1: string =
      (data.addressLine1 && `${data.addressLine1}, `) || '';

    return `${addressLine1}${locationName}${city}${state}`;
  };

  getAddressLine = (data: ILoadStops): string => {
    const addressLine1:any =
      (data.addressLine1 && `${data.addressLine1}, `) || null;

    return addressLine1;
  };
  getStopID = (data: ILoadStops, shipmentId: any): string => {
    const stopID: any = data.stopId || `${shipmentId}_${data.stopSequence || data.sequenceNo}_${data.stopType}`;
    return stopID;
  };

  getLatLng = (data: ILoadStops) => {
    return (
      (data.longitude && {
        lat: parseFloat(data.latitude) || '',
        lng: parseFloat(data.longitude) || '',
      }) ||
      undefined
    );
  };

  getLocations = (data: ILoadMapProps): Partial<ILoadMapState> => {
    let { loadStop = [], shipmentId } = data;
    let origin: DirectionLocation = {
      locationName: '',
      locationType: '',
      index: -1,
      isMarkerAdded: false,
      latLng: { lat: 0, lng: 0 },
      postalCode: '',
      city: '',
      state: '',
      shipmentId: '',
      stopId: '',
      addressLine1: null
    };
    let destination: DirectionLocation = {
      locationName: '',
      locationType: '',
      index: -1,
      isMarkerAdded: false,
      latLng: { lat: 0, lng: 0 },
      postalCode: '',
      city: '',
      state: '',
      shipmentId: '',
      stopId: '',
      addressLine1: null
    };
    const waypoints: DirectionsWaypoint[] = [];
    const totalPoints = loadStop.length;
    loadStop = loadStop.slice().sort((load1: ILoadStops, load2: ILoadStops) => {
      if (load1.sequenceNo && load2.sequenceNo) {
        return load1.sequenceNo - load2.sequenceNo;
      }
      if (load1.stopSequence && load2.stopSequence) {
        return load1.stopSequence - load2.stopSequence;
      }
      return 0;
    });

    loadStop.forEach((item: ILoadStops, index: number) => {
      const addrLine = this.getAddressLine(item);
      if (index === 0) {
        origin = {
          locationName: this.getLocationName(item),
          locationType: item.stopType || '',
          latLng: this.getLatLng(item),
          index,
          isMarkerAdded: false,
          postalCode: item.postalCode,
          city: item.city,
          state: item.state,
          shipmentId,
          stopId: item.stopId || `${shipmentId}_${item.stopSequence || item.sequenceNo}_${item.stopType}`,
        };
        if(addrLine) {
          origin.addressLine1 = this.getAddressLine(item);
        }
      } else if (index + 1 === totalPoints) {
        destination = {
          locationName: this.getLocationName(item),
          locationType: item.stopType || '',
          index,
          isMarkerAdded: false,
          latLng: this.getLatLng(item),
          postalCode: item.postalCode,
          city: item.city,
          state: item.state,
          shipmentId,
          stopId: this.getStopID(item,shipmentId),
        };
        if(addrLine) {
          destination.addressLine1 = this.getAddressLine(item);
        }
      } else {
        waypoints.push({
          location: addrLine ? {
            addressLine1 : addrLine,
            locationName: this.getLocationName(item),
            locationType: item.stopType || '',
            index,
            isMarkerAdded: false,
            latLng: this.getLatLng(item),
            postalCode: item.postalCode,
            city: item.city,
            state: item.state,
            shipmentId,
            stopId: this.getStopID(item,shipmentId),
          } : {
            locationName: this.getLocationName(item),
            locationType: item.stopType || '',
            index,
            isMarkerAdded: false,
            latLng: this.getLatLng(item),
            postalCode: item.postalCode,
            city: item.city,
            state: item.state,
            shipmentId,
            stopId: this.getStopID(item,shipmentId)
          },
          stopover: true
        });
      }
    });

    return { origin, destination, waypoints };
  };

  render() {
    const {
      origin,
      destination,
      waypoints,
      isMapVisible
    } = this.state;
    const isDesktop = isPlatform('desktop');
    const bounds = [
      {
        origin,
        destination,
      }
    ]
    return (
      <>
        <IonLoading
          isOpen={this.state.usePCmilerMaps ? !this.state.isShowMap : !isMapVisible}
          spinner={null}
          message={`<img src= "assets/images/Schneider_Spinner-transparent.gif" className="loader-image" />`}
          show-backdrop={false}
          cssClass="loader-message"
        />
        <div
          className="load-map"
          style={{
            display: `${isMapVisible ? 'block' : 'none'}`,
            position: 'relative'
          }}
        >
          <IonGrid
            className="map-grid"
            style={{
              height: '250px',
              overflow: 'hidden',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%'
            }}
          >
            {this.state.isShowMap && this.props.loadStop && this.props.loadStop.length > 0 && (
              this.state.usePCmilerMaps ? <PCMilerLoadMap
                waypoints={waypoints}
                origin={origin}
                destination={destination}
                fullscreenControl={this.props.fullScreenControl}
              />
	      : <GoogleMap
                zoom={10}
                mapTypeControl={false}
                streetViewControl={false}
                mapTypeId={MapType.roadmap}
                zoomControl={isDesktop}
                fullscreenControl
                bounds={bounds}
                waypoints={waypoints}
                isMyload={this.props.isCarrierLoad}
                isLimitedZoom = {!(origin.addressLine1 && destination.addressLine1)}
              >
              </GoogleMap>
            )}
          </IonGrid>
        </div>
      </>
    );
  }
}